import { useState, useEffect, useRef, useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import NotificheBell from './NotificheBell'
import { getRoleConfig } from '../../utils/roleUtils'
import {
  LayoutDashboard, FolderKanban, Calendar, BookUser, Users, UserCheck, Inbox,
  Wallet, HardDrive, UsersRound, CheckSquare, Settings, LogOut,
  Share2, StickyNote, MessageSquare, Film, FileText, Palette, Clapperboard, Wrench,
} from 'lucide-react'

const MIN_W = 56
const MAX_W = 360
const SNAP_THRESHOLD = 120
const DEFAULT_W = 256

const sharedNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/bacheca', label: 'Bacheca', icon: Inbox, badgeRichieste: true },
  { to: '/progetti', label: 'Progetti', icon: FolderKanban },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare },
  { to: '/calendario', label: 'Calendario', icon: Calendar },
  { to: '/chat', label: 'Chat', icon: MessageSquare, badge: true },
  { to: '/revisioni', label: 'Revisioni', icon: Film },
  { to: '/storage', label: 'Storage', icon: HardDrive },
  { to: '/moodboard', label: 'Moodboard', icon: Palette },
  { to: '/note', label: 'Note', icon: StickyNote },
  { to: '/montati', label: 'Bollettino', icon: Clapperboard },
  { to: '/tools', label: 'Tools', icon: Wrench },
]

const adminNavItems = [
  { to: '/finance', label: 'Finance', icon: Wallet },
  { to: '/preventivi', label: 'Preventivi', icon: FileText },
  { to: '/rubrica', label: 'Rubrica', icon: BookUser },
  { to: '/social', label: 'Social', icon: Share2 },
  { to: '/creativi', label: 'Creativi', icon: Users },
  { to: '/clienti', label: 'Clienti', icon: UserCheck },
  { to: '/team', label: 'Team', icon: UsersRound },
]

const Sidebar = ({ width, onWidthChange }) => {
  const { profile, signOut, getDisplayName } = useAuth()
  const location = useLocation()
  const [unreadCount, setUnreadCount] = useState(0)
  const [richiesteNuove, setRichiesteNuove] = useState(0)
  const channelRef = useRef(null)
  const isDragging = useRef(false)

  const isCollapsed = width <= SNAP_THRESHOLD
  const displayName = getDisplayName()
  const avatarLetter = displayName?.[0]?.toUpperCase() ?? '?'
  const avatarUrl = profile?.avatar_url ? `${profile.avatar_url}?t=${Date.now()}` : null
  const myId = profile?.id
  const isAdmin = profile?.role === 'admin'

  /* ── Unread badge ── */
  const fetchUnread = useCallback(async () => {
    if (!myId) return
    const { count } = await supabase
      .from('chat_messaggi')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', myId)
      .is('letto_at', null)
    setUnreadCount(count ?? 0)
  }, [myId])

  useEffect(() => {
    if (location.pathname === '/chat') setUnreadCount(0)
  }, [location.pathname])

  useEffect(() => {
    if (!myId) return
    fetchUnread()
    if (channelRef.current) supabase.removeChannel(channelRef.current)
    const ch = supabase.channel(`sidebar-unread-${myId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messaggi', filter: `receiver_id=eq.${myId}` },
        () => { if (window.location.pathname !== '/chat') setUnreadCount(p => p + 1) })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_messaggi', filter: `receiver_id=eq.${myId}` },
        () => fetchUnread())
      .subscribe()
    channelRef.current = ch
    return () => { supabase.removeChannel(ch) }
  }, [myId, fetchUnread])

  /* ── Badge bacheca: quante richieste nuove dal sito ── */
  useEffect(() => {
    if (!myId) return undefined
    const fetchRichieste = async () => {
      const { count } = await supabase
        .from('richieste')
        .select('id', { count: 'exact', head: true })
        .eq('stato', 'nuova')
      setRichiesteNuove(count ?? 0)
    }
    fetchRichieste()
    const ch = supabase.channel(`sidebar-richieste-${myId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'richieste' }, () => fetchRichieste())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [myId])

  /* ── Drag handle ── */
  const handleDragStart = useCallback((e) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = width
    isDragging.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const onMove = (ev) => {
      const newW = Math.min(MAX_W, Math.max(MIN_W, startW + ev.clientX - startX))
      onWidthChange(newW)
    }

    const onUp = (ev) => {
      isDragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      const rawW = Math.min(MAX_W, Math.max(MIN_W, startW + ev.clientX - startX))
      const snapped = rawW < SNAP_THRESHOLD ? MIN_W : rawW
      onWidthChange(snapped)
      localStorage.setItem('sidebarWidth', String(snapped))
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [width, onWidthChange])

  const toggleCollapse = useCallback(() => {
    const newW = isCollapsed ? DEFAULT_W : MIN_W
    onWidthChange(newW)
    localStorage.setItem('sidebarWidth', String(newW))
  }, [isCollapsed, onWidthChange])

  /* ── NavItem renderer ── */
  const renderNavItem = ({ to, label, icon: Icon, badge, badgeRichieste }) => {
    const count = badgeRichieste ? richiesteNuove : unreadCount
    const showBadge = (badge || badgeRichieste) && count > 0
    return (
      <NavLink
        key={to}
        to={to}
        end={to === '/dashboard'}
        title={isCollapsed ? label : undefined}
        className={({ isActive }) =>
          `flex items-center rounded-lg text-sm font-medium transition-colors ${
            isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'
          } ${isActive ? 'bg-violet-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`
        }
      >
        <div className="relative flex-shrink-0">
          <Icon size={18} />
          {showBadge && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </div>
        {!isCollapsed && <span className="flex-1 truncate">{label}</span>}
        {!isCollapsed && showBadge && (
          <span className="text-[10px] bg-red-500/20 text-red-400 font-semibold px-1.5 py-0.5 rounded-full">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </NavLink>
    )
  }

  return (
    <div
      className="h-screen bg-gray-900 border-r border-gray-800 flex flex-col fixed left-0 top-0 z-30"
      style={{ width: width + 'px' }}
    >
      {/* Logo */}
      <div className={`border-b border-gray-800 flex items-center overflow-hidden ${isCollapsed ? 'justify-center py-4 px-2' : 'px-5 py-4'}`}>
        {isCollapsed
          ? <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">P</div>
          : <img src="/PLUTONIC_LOGO_WHITE_NOBG.png" alt="Plutonic Studio" className="h-24 w-auto object-contain" />
        }
      </div>

      {/* Nav */}
      <nav className={`flex-1 py-4 overflow-y-auto overflow-x-hidden ${isCollapsed ? 'px-2' : 'px-3'}`}>
        <div className="space-y-1">
          {sharedNavItems.map(item => renderNavItem(item))}
        </div>

        {isAdmin && (
          <>
            <div className="my-3 border-t border-gray-800/80" />
            {!isCollapsed && (
              <p className="text-[10px] text-gray-600 uppercase font-semibold tracking-widest px-3 mb-2">Gestione</p>
            )}
            <div className="space-y-1">
              {adminNavItems.map(item => renderNavItem(item))}
            </div>
          </>
        )}
      </nav>

      {/* Notifiche */}
      <div className={`pb-1 flex ${isCollapsed ? 'justify-center px-2' : 'justify-end px-3'}`}>
        <NotificheBell />
      </div>

      {/* Profilo */}
      <div className={`py-4 border-t border-gray-800 ${isCollapsed ? 'px-2' : 'px-3'}`}>
        <NavLink
          to="/impostazioni"
          title={isCollapsed ? `${displayName} — Impostazioni` : undefined}
          className={({ isActive }) =>
            `flex items-center rounded-lg mb-1 transition-colors ${
              isCollapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2'
            } ${isActive ? 'bg-gray-800' : 'hover:bg-gray-800'}`
          }
        >
          <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white text-sm font-bold overflow-hidden flex-shrink-0">
            {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" /> : avatarLetter}
          </div>
          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{displayName}</p>
                <p className={`text-xs truncate font-medium ${getRoleConfig(profile?.role).className}`}>{getRoleConfig(profile?.role).label}</p>
              </div>
              <Settings size={15} className="text-gray-500 flex-shrink-0" />
            </>
          )}
        </NavLink>

        <button
          onClick={signOut}
          title={isCollapsed ? 'Esci' : undefined}
          className={`flex items-center rounded-lg text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors w-full ${
            isCollapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2.5'
          }`}
        >
          <LogOut size={18} />
          {!isCollapsed && <span>Esci</span>}
        </button>
      </div>

      {/* Drag handle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 group"
        onMouseDown={handleDragStart}
        onDoubleClick={toggleCollapse}
      >
        <div className="absolute inset-y-0 left-0 right-0 group-hover:bg-violet-500/40 transition-colors rounded-full" />
      </div>
    </div>
  )
}

export default Sidebar
